import styled, { css } from 'styled-components/macro';

import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

import { download_small, pen_hollow } from '../../assets/icons';
import { NavigationOptions } from 'swiper/types/modules/navigation';
import { ThumbsOptions } from '../../types';
import { SwiperModule } from 'swiper/types/shared';
import { FreeModeOptions } from 'swiper/types/modules/free-mode';
import { RendererPropsData } from './types';

const getSwiperCarouselHeight = (slides: number) => {
  switch (slides) {
    case 1:
      return 80;
    case 2:
      return 170;
    case 3:
      return 260;
    default:
      return 354;
  }
};

export const Scroll = css`
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    height: 20px;
    background: #2675b6ff;
    border-radius: 5px;
  }
`;

const SinglePageSettings = css`
  .pdf-container {
    top: 50%;
    transform: translateY(-50%);
  }

  .react-pdf__Page {
    background-color: #1f2939 !important;

    canvas {
      margin-left: auto;
    }
  }
`;

export const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  background-color: #1f2939;
  z-index: 1001;

  #header-bar {
    background-color: #ffffff;
    min-height: 46px;
  }

  .previewer {
    background-color: #1f2939;
  }

  .spinner_wrapper {
    display: flex;
    flex-direction: column;
    align-content: center;
    justify-content: center;
    gap: 16px;

    label {
      position: absolute;
      top: 44%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: #ffffff;
      font-size: 16px;
    }
  }
`;

export const StyledSwiper = styled(Swiper)<{
  /*
    Пока npm пакет react-tooltip не обновит в своих зависимостях typescript до версии 5+ (необходимая для swiper),
    большинство пропсов необходимо типизировать вручную
  */
  allowTouchMove?: boolean;
  spaceBetween?: number | string;
  navigation?: NavigationOptions | boolean;
  thumbs: ThumbsOptions;
  modules?: SwiperModule[];
  $transition: boolean;
  initialSlide?: number;
}>`
  position: absolute;
  height: 100vh;
  width: 100vw;
  box-sizing: border-box;
  ${SinglePageSettings}

  .swiper-slide {
    opacity: ${(props) => (props.$transition ? 0 : 1)};
  }

  .docx {
    box-sizing: border-box !important;
    width: 45vw !important;
    margin-left: -5vw;
    margin-bottom: 0 !important;
  }

  .docx-wrapper {
    padding: 0 0 0 16px;
    background: none;

    img {
      max-width: calc(45vw - 150px) !important;
    }
  }

  .swiper-button-next,
  .swiper-button-prev {
    height: 64px;
    width: 64px;
    border-radius: 50%;
    background-color: #ffffff;
    margin: 0 24px;

    &:after {
      font-size: 20px;
      font-weight: 900;
      color: #697180;
    }
  }

  .swiper-button-next:after {
    margin-left: 2px;
  }

  .swiper-button-prev:after {
    margin-left: -6px;
  }
`;

export const DocumentHeader = styled.div`
  position: absolute;
  top: 12px;
  left: 0;
  height: 40px;
  width: calc(100% - 24px);
  padding: 0 16px;
  margin-left: 12px;
  display: flex;
  align-items: center;
  justify-content: end;
  background-color: #ffffff;
  color: #0f1728;
  box-sizing: border-box;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export const DocumentHeaderContent = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 2;
`;

export const StyledSwiperCarousel = styled(Swiper)<{
  /*
    Пока npm пакет react-tooltip не обновит в своих зависимостях typescript до версии 5+ (необходимая для swiper),
    большинство пропсов необходимо типизировать вручную
  */
  allowTouchMove?: boolean;
  spaceBetween?: number | string;
  modules?: SwiperModule[];
  slidesPerView?: number | 'auto';
  freeMode?: FreeModeOptions | boolean;
  watchSlidesProgress?: boolean;
  direction?: 'horizontal' | 'vertical';
  slides: number;
}>`
  position: absolute;
  width: 120px;
  height: ${(props) => getSwiperCarouselHeight(props.slides)}px;
  margin-left: calc(65vw + 32px);
  margin-top: 35px;

  .swiper-wrapper {
    height: min-content;
    overflow: hidden;
  }

  .swiper-slide {
    overflow: hidden;
  }

  .docx-wrapper {
    cursor: pointer;

    * {
      //padding: 0 !important;
      max-width: 120px !important;
      max-height: 80px !important;
      font-size: 8px !important;
    }

    .docx_textbody {
      margin-bottom: 0 !important;
    }

    .docx_heading1 {
      margin-top: 0 !important;
    }
  }

  .docx-wrapper,
  .docx {
    min-height: 80px !important;
    width: 120px !important;
    height: 80px !important;
    padding: 0 !important;
  }

  .xlsx-container {
    padding: 0;
    width: 100%;
    overflow: hidden;
  }

  .xlsx-table {
    max-width: 120px;
    font-size: 8px;

    td {
      padding: 0;
    }
  }

  .stab {
    min-width: 120px;
    max-width: 120px;
    font-size: 10px;
    padding: 12px;
    box-sizing: border-box;
  }

  canvas {
    width: 100% !important;
  }
`;

const PreviewHeaderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #ced5de;
  background: #ffffff;
  cursor: pointer;
`;

export const EditButton = styled(PreviewHeaderButton)`
  &:before {
    content: url(${pen_hollow});
  }
`;

export const DeleteButton = styled(PreviewHeaderButton)`
  i {
    color: #697180;
  }
`;

export const DownloadButton = styled(PreviewHeaderButton)`
  &:before {
    content: url(${download_small});
  }
`;

export const ClosePreviewWindowButton = styled(PreviewHeaderButton)`
  &:before {
    display: inline-block;
    content: '\\00d7';
    font-size: 25px;
    color: #697180;
  }
`;

export const DocumentName = styled.div`
  color: #0f1728;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export const DocumentNameInput = styled.input`
  border: none;
  outline: none;
`;

const Container = styled.div`
  width: 45vw;
  height: calc(100vh - 100px) !important;
  overflow: auto;
  ${Scroll}
`;

export const DocumentRendererContainer = styled(Container)<{
  slidesCount: number;
}>`
  margin: ${(props) =>
    props.slidesCount > 1 ? '75px 0 0 20vw' : '75px auto 0 auto'};
`;

export const ThumbnailSwiperSlide = styled(SwiperSlide)<{ $active: boolean }>`
  overflow: hidden;
  box-sizing: border-box;
  cursor: pointer;
  min-height: 80px !important;

  ${(props) =>
    props.$active
      ? css`
          border: 1px solid #ffffff;
        `
      : css`
          opacity: 0.6;
        `}
`;

export const Stub = styled.div<{ fileObj: RendererPropsData }>`
  height: 100%;
  min-width: 45vw;
  max-width: 1000px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  background-color: #ffffff;
`;
